import {useMemo} from 'react';

import {mapWorkforceProfilesStatusResponse} from '../serializers/WorkforceProfileSerializers';
import {CompanyWorkforceProfile} from '../types/WorkforceProfileTypes';
import {isWorkforceProfileAnalysisInProgress} from '../utils/WorkforceProfiles/workforceProfileStatusUtils';

import {useApiRead} from '../../../../hooks/useApiRead';
import {GetRoutes} from '../../../../lib/api/GET/GetRoutes';

const STATUS_POLL_INTERVAL_MS = 30000;

const mergeProfileStatusUpdate = (
  base: CompanyWorkforceProfile,
  update: CompanyWorkforceProfile
): CompanyWorkforceProfile => ({
  ...base,
  status: update.status ?? base.status,
  analyzedAt: update.analyzedAt ?? base.analyzedAt,
  analysisVersion: update.analysisVersion ?? base.analysisVersion,
  alignment: update.alignment ?? base.alignment,
  cultureType: update.cultureType ?? base.cultureType,
});

type Props = {
  companyId: string;
  profiles: CompanyWorkforceProfile[];
};

export const useWorkforceProfilesStatusPolling = ({companyId, profiles}: Props) => {
  const inProgressIds = useMemo(
    () =>
      profiles
        .filter((profile) => isWorkforceProfileAnalysisInProgress(profile.status))
        .map((profile) => profile.resourceId),
    [profiles]
  );

  const sortedInProgressIds = useMemo(() => [...inProgressIds].sort(), [inProgressIds]);

  const statusPath = useMemo(
    () =>
      companyId && sortedInProgressIds.length > 0
        ? GetRoutes.companyWorkforceProfilesStatus(companyId, sortedInProgressIds)
        : '',
    [companyId, sortedInProgressIds]
  );

  const {data: statusUpdates} = useApiRead<CompanyWorkforceProfile[]>({
    path: statusPath,
    camelCaseOverride: mapWorkforceProfilesStatusResponse,
    options: {
      enabled: !!companyId && sortedInProgressIds.length > 0,
      queryKey: ['workforceProfilesStatus', companyId, sortedInProgressIds],
      refetchInterval: sortedInProgressIds.length > 0 ? STATUS_POLL_INTERVAL_MS : false,
    },
  });

  const mergedProfiles = useMemo(() => {
    if (!statusUpdates?.length) {
      return profiles;
    }

    const updatesById = new Map(statusUpdates.map((profile) => [profile.resourceId, profile]));

    return profiles.map((profile) => {
      const update = updatesById.get(profile.resourceId);
      return update ? mergeProfileStatusUpdate(profile, update) : profile;
    });
  }, [profiles, statusUpdates]);

  return {mergedProfiles};
};
